import { Component, OnDestroy, OnInit, computed, inject, signal, viewChild } from "@angular/core";
import { RouterLink, RouterLinkActive } from "@angular/router";
import { NgbTooltipModule } from "@ng-bootstrap/ng-bootstrap";
import { TranslocoModule, TranslocoService } from "@jsverse/transloco";
import { Store } from "@ngxs/store";
import { Observable } from "rxjs";
import { Router } from "@angular/router";
import { AuthService } from "@auth0/auth0-angular";
import { UserIdleService } from "@sdk/lib/user-idle/user-idle.service";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { MyNotificationsComponent } from "@sdk/lib/my-notifications/my-notifications.component";
import { AssistantChatbotService } from "../assistant-chatbot/assistant-chatbot.service";
import { Group } from "@sdk/lib/types/group";
import { UpliftNotificationItem } from "@sdk/lib/types/notification";
import { CourseMenuItem } from "@sdk/lib/types/course";
import {
  LOGIN_PATH,
  UPDATE_PROFILE,
  COURSE_MENU_ITEM_ROUTE,
  EMERGENCY_HELP_PATH,
  COURSE_SESSIONS_PATH,
  HOME_PATH,
  MY_JOURNALS_PATH,
} from "@sdk/lib/values/shared-paths";
import { GroupState } from "../../states/group-state";
import { MyNotificationsListState } from "../../states/my-notifications-list.state";
import { FetchMyNotificationsList } from "../../actions/notification.actions";
import { ROUTES } from "../vertical-menu/vertical-menu-routes.config";
import { RouteInfo } from "../vertical-menu/vertical-menu.metadata";

@Component({
  selector: "app-sanctuary-sidebar",
  templateUrl: "./sanctuary-sidebar.component.html",
  styleUrls: ["./sanctuary-sidebar.component.scss"],
  imports: [RouterLink, RouterLinkActive, NgbTooltipModule, MyNotificationsComponent, TranslocoModule],
  host: {
    "[class.collapsed]": "isCollapsed()",
    "[class.no-transition]": "noTransition()",
  },
})
export class SanctuarySidebarComponent implements OnInit, OnDestroy {
  private _store = inject(Store);
  private _router = inject(Router);
  private _auth = inject(AuthService);
  private _userIdleService = inject(UserIdleService);
  private _chatbotService = inject(AssistantChatbotService);
  private _transloco = inject(TranslocoService);

  private readonly _navTitleKeys: Record<string, string> = {
    [`/${HOME_PATH}`]: "shell.nav.dashboard",
    [`/${COURSE_SESSIONS_PATH}`]: "shell.nav.courseSession",
    [`/${MY_JOURNALS_PATH}`]: "shell.nav.myJourney",
    [`/${EMERGENCY_HELP_PATH}`]: "shell.nav.emergencyHelp",
  };

  private _localiseRoute(route: RouteInfo): RouteInfo {
    return { ...route, titleKey: this._navTitleKeys[route.path] };
  }

  private static readonly COLLAPSED_KEY = "sn-sidebar-collapsed";
  private static readonly DARK_MODE_KEY = "sn-dark-mode";

  private readonly _group = this._store.selectSignal<Group>(GroupState as any);
  readonly myNotificationsList$: Observable<UpliftNotificationItem[]> = this._store.select(
    MyNotificationsListState as any,
  );

  readonly isCollapsed = signal(
    !window.matchMedia("(min-width: 1200px)").matches ||
      localStorage.getItem(SanctuarySidebarComponent.COLLAPSED_KEY) === "true",
  );
  readonly noTransition = signal(true);
  readonly isExpanded = computed(() => !this.isCollapsed());
  readonly isNotificationOpen = signal(false);
  readonly isWindowBlurred = signal(false);
  readonly isDarkMode = signal(
    localStorage.getItem(SanctuarySidebarComponent.DARK_MODE_KEY) === "true",
  );
  readonly hasAssistant = computed(() => !!this._group()?.course?.assistant_url);
  readonly courseBrandName = computed(() => {
    switch (this._group()?.course?.course_brand) {
      case "uplift":
        return "UpLift";
      case "uplift_now":
        return "UpLiftNow";
      default:
        return null;
    }
  });
  readonly brandLogo = computed(() =>
    this.courseBrandName() ? "assets/img/logo.png" : "assets/img/logos/mindlife-mark.png",
  );

  readonly notificationsComp = viewChild<MyNotificationsComponent>("notificationsComp");

  private _dashboardOnlyRoutes: RouteInfo[] = ROUTES.filter(
    (route) => route.path === `/${HOME_PATH}`,
  );

  readonly courseMenuItemRoutes = computed<RouteInfo[]>(() => {
    const group = this._group();
    if (!group) {
      return this._dashboardOnlyRoutes.map((route) =>
        this._localiseRoute(route),
      );
    }

    const routes = ROUTES.map((route) => this._localiseRoute(route));
    const COURSE_MENU_ITEM_LIST: CourseMenuItem[] =
      group?.course?.course_menu_item_list;
    if (COURSE_MENU_ITEM_LIST?.length === 0) {
      return routes;
    }
    const SESSION_LIST_TITLE = group?.course?.session_list_title;
    if (SESSION_LIST_TITLE) {
      const sessionRoute = routes.find(
        (route) => route.path === `/${COURSE_SESSIONS_PATH}`,
      );
      if (sessionRoute) {
        sessionRoute.title = SESSION_LIST_TITLE;
        sessionRoute.titleKey = undefined;
      }
    }
    const COURSE_MENU_ITEM: RouteInfo[] = [];
    COURSE_MENU_ITEM_LIST?.forEach((courseMenuItem) => {
      COURSE_MENU_ITEM.push({
        path: `/${COURSE_MENU_ITEM_ROUTE}/${courseMenuItem._id}`,
        title: courseMenuItem.title,
        icon: courseMenuItem.icon || "ft-book",
        class: "",
        badge: "",
        badgeClass: "",
        isExternalLink: false,
        submenu: [],
      });
    });

    return [
      ...routes.filter(({ path }) => path !== `/${EMERGENCY_HELP_PATH}`),
      ...COURSE_MENU_ITEM,
      ...routes.filter(({ path }) => path === `/${EMERGENCY_HELP_PATH}`),
    ];
  });

  private _xlQuery = window.matchMedia("(min-width: 1200px)");
  private _mobileQuery = window.matchMedia("(max-width: 580px)");

  closeOverlay(): void {
    if (!this._xlQuery.matches) this._setCollapsed(true);
  }

  ngOnInit(): void {
    this._store.dispatch([new FetchMyNotificationsList()]);
    this._syncBodyClass();
    this._applyDarkMode(this.isDarkMode());

    this._xlQuery.addEventListener("change", this._onViewportChange);
    this._mobileQuery.addEventListener("change", this._onViewportChange);
    window.addEventListener("blur", this._onWindowBlur);
    document.addEventListener("mousemove", this._onMouseMove);

    document.body.classList.add("sidebar-no-transition");
    requestAnimationFrame(() => {
      this.noTransition.set(false);
      document.body.classList.remove("sidebar-no-transition");
    });
  }

  toggleCollapsed(): void {
    this._setCollapsed(!this.isCollapsed());
  }

  private _setCollapsed(collapsed: boolean): void {
    this.isCollapsed.set(collapsed);
    localStorage.setItem(SanctuarySidebarComponent.COLLAPSED_KEY, String(collapsed));
    this._syncBodyClass();
  }

  private _backdropClickHandler = () => this.closeOverlay();

  private _onWindowBlur = () => this.isWindowBlurred.set(true);

  private _onMouseMove = () => {
    if (this.isWindowBlurred()) this.isWindowBlurred.set(false);
  };

  private _onViewportChange = () => {
    if (!this._xlQuery.matches && !this.isCollapsed()) this._setCollapsed(true);
  };

  private _syncBodyClass(): void {
    const collapsed = this.isCollapsed();
    document.body.classList.toggle("sidebar-collapsed", collapsed);

    const isOverlay = !this._xlQuery.matches && !collapsed;
    document.body.classList.toggle("sidebar-overlay-open", isOverlay);

    let backdrop = document.querySelector(".sn-overlay-backdrop") as HTMLElement;
    if (isOverlay && !backdrop) {
      backdrop = document.createElement("div");
      backdrop.className = "sn-overlay-backdrop";
      backdrop.addEventListener("click", this._backdropClickHandler);
      document.body.appendChild(backdrop);
    } else if (!isOverlay && backdrop) {
      backdrop.removeEventListener("click", this._backdropClickHandler);
      backdrop.remove();
    }
  }

  goToProfileEdit(): void {
    this._router.navigate([`/${UPDATE_PROFILE}`]);
  }

  goToLogoutPage(): void {
    Swal.fire({
      title: this._transloco.translate("shell.logout.confirmTitle", {
        app: this._transloco.translate("common.brandName"),
      }),
      text: this._transloco.translate("shell.logout.confirmText"),
      showCancelButton: true,
      confirmButtonText: this._transloco.translate("common.logout"),
      cancelButtonText: this._transloco.translate("common.cancel"),
    }).then((optionSelected) => {
      if (optionSelected.value) {
        this._userIdleService.resetTimer();
        this._userIdleService.stopTimer();
        this._userIdleService.stopWatching();
        this._auth.logout({
          logoutParams: {
            returnTo: window.location.origin + "/" + LOGIN_PATH,
          },
        });
      }
    });
  }

  setTheme(theme: string): void {
    this._setDarkMode(theme === "dark");
  }

  toggleDarkMode(): void {
    this._setDarkMode(!this.isDarkMode());
  }

  private _setDarkMode(isDark: boolean): void {
    this.isDarkMode.set(isDark);
    localStorage.setItem(SanctuarySidebarComponent.DARK_MODE_KEY, String(isDark));
    this._applyDarkMode(isDark);
  }

  private _applyDarkMode(isDark: boolean): void {
    if (isDark) {
      document.documentElement.setAttribute("data-theme", "dark");
    } else {
      document.documentElement.removeAttribute("data-theme");
    }
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute("content", isDark ? "#1c1e27" : "#ffffff");
    }
  }

  toggleAssistant(): void {
    this._chatbotService.toggle();
  }

  toggleNotifications(): void {
    this.notificationsComp()?.toggle();
  }

  onNotificationOpen(isOpen: boolean): void {
    this.isNotificationOpen.set(isOpen);
    if (isOpen && !this._xlQuery.matches && !this.isCollapsed()) this._setCollapsed(true);
  }

  dispatchEvent(): void {
    this._store.dispatch([new FetchMyNotificationsList()]);
  }

  ngOnDestroy(): void {
    this._xlQuery.removeEventListener("change", this._onViewportChange);
    this._mobileQuery.removeEventListener("change", this._onViewportChange);
    window.removeEventListener("blur", this._onWindowBlur);
    document.removeEventListener("mousemove", this._onMouseMove);
    document.body.classList.remove("sidebar-overlay-open");
    const backdrop = document.querySelector(".sn-overlay-backdrop");
    if (backdrop) {
      backdrop.removeEventListener("click", this._backdropClickHandler);
      backdrop.remove();
    }
  }
}
